import { useNavigate, useSearch } from '@tanstack/react-router';
import { DEFAULT_PAGE_SIZE } from '../api/paging';

/**
 * Table state lives in the URL (FE plan §3.4, §3.6): page, page size, sort
 * and filters are search params, so a list query key IS its URL and a view
 * can be linked, bookmarked and back-buttoned.
 *
 *   ?page=2&pageSize=50&sort=joinedAt&dir=desc&status=active&q=ann
 *
 * `parseTableSearch` is a route's `validateSearch` (tolerant: bad values fall
 * back to defaults rather than throwing); `useTableUrlState` reads/writes it.
 * Any filter change resets to page 1.
 */
/** A server sort: the BE `sortBy` key plus direction. */
export interface TableSort {
  key: string;
  dir: 'asc' | 'desc';
}

export interface TableSearch {
  page: number;
  pageSize: number;
  sort?: string;
  dir?: 'asc' | 'desc';
  q?: string;
  [filter: string]: unknown;
}

export interface TableDefaults {
  pageSize?: number;
  pageSizes?: number[];
}

function positiveInt(value: unknown): number | undefined {
  const n = typeof value === 'number' ? value : typeof value === 'string' ? Number(value) : NaN;
  return Number.isInteger(n) && n > 0 ? n : undefined;
}

export function parseTableSearch(search: Record<string, unknown>, defaults: TableDefaults = {}): TableSearch {
  const sizes = defaults.pageSizes ?? [25, 50, 100];
  const size = positiveInt(search.pageSize);
  const out: TableSearch = {
    ...search,
    page: positiveInt(search.page) ?? 1,
    pageSize: size && sizes.includes(size) ? size : (defaults.pageSize ?? DEFAULT_PAGE_SIZE),
  };
  out.sort = typeof search.sort === 'string' && search.sort ? search.sort : undefined;
  out.dir = search.dir === 'asc' || search.dir === 'desc' ? search.dir : undefined;
  if (!out.sort) out.dir = undefined;
  out.q = typeof search.q === 'string' && search.q.trim() ? search.q : undefined;
  return out;
}

/** Drops empty values so the URL only carries what's set. */
function compact(search: Record<string, unknown>): Record<string, unknown> {
  return Object.fromEntries(
    Object.entries(search).filter(
      ([, v]) => v !== undefined && v !== null && v !== '' && !(Array.isArray(v) && !v.length),
    ),
  );
}

export function useTableUrlState(defaults: TableDefaults = {}) {
  const raw = useSearch({ strict: false }) as Record<string, unknown>;
  const navigate = useNavigate();
  const state = parseTableSearch(raw, defaults);

  const update = (patch: Record<string, unknown>) =>
    void navigate({
      to: '.',
      replace: true,
      search: ((prev: Record<string, unknown>) => compact({ ...prev, ...patch })) as never,
    });

  const sort: TableSort | null = state.sort ? { key: state.sort, dir: state.dir ?? 'asc' } : null;
  const { page: _page, pageSize: _pageSize, q: _q, sort: _sort, dir: _dir, ...filters } = state;

  return {
    page: state.page,
    pageSize: state.pageSize,
    q: state.q,
    sort,
    /** Every other search param — the screen's own filters (strings as the URL holds them). */
    filters: filters as Record<string, unknown>,
    setSort: (next: TableSort | null) => update({ sort: next?.key, dir: next?.dir, page: undefined }),
    setPage: (page: number) => update({ page: page > 1 ? page : undefined }),
    setPageSize: (pageSize: number) => update({ pageSize, page: undefined }),
    /** Sets one filter (or `q`) and returns to page 1. */
    setFilter: (key: string, value: unknown) => update({ [key]: value, page: undefined }),
    clearFilters: (keys: string[]) => update(Object.fromEntries([...keys, 'page'].map((k) => [k, undefined]))),
    /** Sets params that are not filters (an open dialog, its tab) — the page is kept. */
    setParams: (patch: Record<string, unknown>) => update(patch),
  };
}
